import { z } from "zod";

import type { ModuleDataMap } from "../response/types";
import type {
  ModuleID,
  ModuleInstanceKey,
  ModuleInstancePosition,
} from "../types/ids";

const LAYOUT_POSITION = "layout";
const CONTENT_POSITION = /^(0|[1-9][0-9]*)$/;

/** Instance key of a view's effective layout module. */
export function layoutInstanceKey(moduleId: ModuleID): ModuleInstanceKey {
  return `${LAYOUT_POSITION}.${moduleId}`;
}

/** Instance key of the module at `index` in the view's `modules` output. */
export function contentInstanceKey(
  index: number,
  moduleId: ModuleID,
): ModuleInstanceKey {
  return `${index}.${moduleId}`;
}

/**
 * Split an instance key into its position and module id. Module ids may
 * contain `.`, so only the first `.` separates the two.
 */
export function parseModuleInstanceKey(
  key: string,
): { position: ModuleInstancePosition; moduleId: ModuleID } | undefined {
  const separator = key.indexOf(".");
  if (separator === -1) {
    return undefined;
  }
  const position = key.slice(0, separator);
  const moduleId = key.slice(separator + 1);
  if (moduleId.length === 0) {
    return undefined;
  }
  if (position === LAYOUT_POSITION) {
    return { position, moduleId };
  }
  if (CONTENT_POSITION.test(position)) {
    return { position: `${Number(position)}`, moduleId };
  }
  return undefined;
}

/**
 * Data for one module instance. A map with no instance keys at all comes from
 * a server that predates them (FEP2-4678 rollout), so read its bare module-id
 * key. Otherwise an absent instance key means the instance has no data — JSON
 * drops `undefined` — and must not borrow another instance's.
 */
export function readModuleData(
  map: ModuleDataMap,
  instanceKey: ModuleInstanceKey,
  moduleId: ModuleID,
): unknown {
  if (Object.hasOwn(map, instanceKey)) {
    return map[instanceKey];
  }
  return Object.keys(map).some(isModuleInstanceKey) ? undefined : map[moduleId];
}

export function isModuleInstanceKey(value: unknown): value is ModuleInstanceKey {
  return (
    typeof value === "string" && parseModuleInstanceKey(value) !== undefined
  );
}

// `z.custom` rather than a refined `z.string()`: `satisfies z.ZodType<T>`
// also checks the input type, which a string schema would widen.
export const ModuleInstanceKeySchema = z.custom<ModuleInstanceKey>(
  isModuleInstanceKey,
  { message: "Invalid module instance key" },
);
